import { toast } from 'sonner';

/**
 * Shared "open external navigation" helper — the single place that builds a
 * Google Maps URL and opens it, so every Navigate button (admin order list,
 * route optimizer, delivery boy order detail/list, customer driver-tracking)
 * shares one implementation instead of each re-deriving its own logic.
 *
 * Coordinates-only, deliberately: address text can drift from the actual pin
 * (typos, a customer editing the text after picking a location, a stale
 * geocode) and silently sends a delivery partner to the wrong place with no
 * indication anything's off. Latitude/longitude are the one field that's
 * always exactly where the map was tapped/searched/apartment-pinned, so
 * they're the only source of truth this opens with — no address/full_address
 * fallback. If they're missing or not valid numbers, this shows an error
 * instead of guessing from text.
 *
 * A `https://www.google.com/maps/search/?api=1&query=lat,lng` link is used
 * deliberately over a `geo:`/`maps://` scheme: on both iOS and Android it's
 * intercepted as a universal/app link and opens the Google Maps app when
 * installed, and degrades gracefully to the Google Maps website when it
 * isn't — satisfying "prefer Google Maps, fall back to whatever's available"
 * without needing separate per-platform URL schemes or feature-detection.
 *
 * @param {{latitude?: number|string|null, longitude?: number|string|null} | null | undefined} location
 * @returns {boolean} whether navigation was actually opened
 */
export function openNavigation(location) {
  const lat = Number(location?.latitude);
  const lng = Number(location?.longitude);
  const isValid = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);

  if (!isValid) {
    toast.error('Location coordinates are not available for this address.');
    return false;
  }

  window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  return true;
}
